import { FC } from 'react'

import { Box, Card, Divider, Skeleton } from '@mui/material'

import { SectionCard } from '../SectionCard/SectionCard'

/**
 * Placeholder for `CreativeAdDetailsDialog`. Mirrors the dialog's structure
 * (info strip → video + timeline + side panel grid → creative-summary cards
 * → tag-dimension list) so the full-screen dialog does not collapse to a
 * lonely centred spinner while the ad payload loads.
 */
export const CreativeAdDetailsSkeleton: FC = () => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
    <Card variant="outlined">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
        <Skeleton variant="rounded" width={56} height={56} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Skeleton variant="text" width="40%" height={22} />
          <Skeleton variant="text" width="60%" height={18} />
        </Box>
        <Skeleton variant="rounded" width={120} height={36} />
      </Box>
      <Divider />
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) auto minmax(0, 1fr)' },
          gap: 2,
          p: 2,
        }}
      >
        <Skeleton variant="rounded" width="100%" height={320} />
        <Divider orientation="vertical" flexItem />
        <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Skeleton variant="text" width={180} height={22} />
          <Skeleton variant="rounded" width="100%" height={120} />
          <Skeleton variant="rounded" width="100%" height={88} />
        </Box>
      </Box>
    </Card>

    <SectionCard title={<Skeleton variant="text" width={200} height={24} />}>
      <Skeleton variant="text" width="100%" />
      <Skeleton variant="text" width="92%" />
      <Skeleton variant="text" width="78%" />
    </SectionCard>

    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
      <SectionCard title={<Skeleton variant="text" width={160} height={24} />}>
        <Skeleton variant="text" width="100%" />
        <Skeleton variant="text" width="85%" />
        <Skeleton variant="text" width="70%" />
      </SectionCard>
      <SectionCard title={<Skeleton variant="text" width={180} height={24} />}>
        <Skeleton variant="text" width="100%" />
        <Skeleton variant="text" width="85%" />
        <Skeleton variant="text" width="70%" />
      </SectionCard>
    </Box>

    <SectionCard title={<Skeleton variant="text" width={220} height={24} />}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {Array.from({ length: 4 }).map((_, i) => (
          <Box key={i} sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <Skeleton variant="text" width="30%" height={20} />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {Array.from({ length: 6 }).map((__, j) => (
                <Skeleton key={j} variant="rounded" width={80 + (j % 3) * 24} height={24} />
              ))}
            </Box>
          </Box>
        ))}
      </Box>
    </SectionCard>
  </Box>
)
